// import type { IR3Promise } from "r3utils";
// import { startDebugServer } from "r3utils";
type R3FetchHeaderValue = string | string[] | number | boolean | null;
type RawR3FetchHeaders = Record<string, R3FetchHeaderValue>;

// export class R3FetchHeaders {
//   constructor(
//       headers?: RawR3FetchHeaders | R3FetchHeaders,
//       defaultHeaders?: RawR3FetchHeaders | R3FetchHeaders
//   );

//   set()
// }

// export interface R3FetchResponse<T = any, C = any> {
//   data: T;
//   status: number;
//   statusText: string;
//   headers: RawAxiosResponseHeaders | AxiosResponseHeaders;
//   config: AxiosRequestConfig<C>;
//   request?: any;
// }

export interface R3FetchDataInterface {
  writeconsole(text: string): void;

  /**
   * GET: Realiza uma consulta no servidor
   *
   * @template Success -> Retorno que será obtido
   * @template {object} Params -> Tipo dos parâmetros que deve ser enviado
   */
  get<Success, Params = object>(
    endpoint: string,
    config?: { params: Params }, // TODO tornar genérico o config, retinar a dependência do axios
    isAsync?: boolean,
    sendAccessToken?: boolean,
  ): Promise<Success>;
  /**
   * POST: Envia dados ao servidor
   * No post deve-se enviar o objeto direto, sem necessidade do params
   * @template R -> Resposta esperada
   * @template {object} P -> Parâmetros requeridos
   * @param params
   */
  post<R, P = object>(
    endpoint: string,
    params?: P,
    isAsync?: boolean,
  ): Promise<R>;
  /**
   *  PUT: Faz a substituição completa do recurso no servidor, alterando todos os elementos
   * @template R -> Genérico com a resposta
   * @template {object} P -> Parâmetros requeridos
   * @param params
   */
  put<R, P = object>(
    endpoint: string,
    params?: P,
    isAsync?: boolean,
  ): Promise<R>;
  /**
   *  PATCH: Faz a substituição parcial do recurso no servidor, alterando apenas o enviado
   * @param R -> Genérico com a resposta
   * @param P -> Parâmetros requeridos
   * @param params
   */
  patch<R, P = object>(
    endpoint: string,
    params?: P,
    isAsync?: boolean,
  ): Promise<R>;
  onError<E>(Error: E): void;
}

export abstract class R3FetchDataBaseImpl implements R3FetchDataInterface {
  // private debugServer: DebugServer;
  private readonly deviceStore = useDeviceStore();

  constructor(protected baseUrl: string = "") {}

  writeconsole(text: string) {
    console.log(text);
  }

  /**
   * Executa uma requisição GET para o endpoint especificado.
   *
   * Este método envia uma requisição GET autenticada para o endpoint da API
   * fornecido. Se nenhum token de autenticação for encontrado, redirecionará o
   * usuário para a página de login.
   *
   * @template Success - O tipo esperado dos dados de resposta em caso de
   *   sucesso
   * @template Params - O tipo de parâmetros a serem enviados na requisição
   * @param {Routes} endpoint - O endpoint da API para fazer a requisição
   * @param {R3AxiosRequestConfig<Params>} [config] - Configuração opcional do
   * @param {isAsync} - Indica se vai executar de modo assíncrono
   *   axios para a requisição
   * @returns {Promise<Success>} Uma promise que resolve com os dados da
   *   resposta
   *
   * @example
   * ```typescript
   * // Obter uma lista de usuários
   * const usuarios = await r3Axios.get<Usuario[], void>('/api/usuarios');
   *
   * // Obter um usuário específico com parâmetros de consulta
   * const usuario = await r3Axios.get<Usuario, { id: number
   *   }>('/api/usuarios/detalhes', { params: { id: 123 }
   * });
   * ```
   */
  abstract get<T = unknown>(
    endpoint: string,
    params?: object | undefined,
    isAsync?: boolean,
  ): Promise<T>;

  /**
   * Executa uma requisição POST para o endpoint especificado.
   *
   * Este método envia uma requisição POST autenticada para o endpoint da API
   * fornecido, enviando dados no corpo da requisição. Se nenhum token de
   * autenticação for encontrado, redirecionará o usuário para a página de login.
   *
   * @template R - O tipo esperado dos dados de resposta em caso de sucesso
   * @template P - O tipo dos parâmetros a serem enviados no corpo da requisição
   * @param {string} endpoint - O endpoint da API para fazer a requisição
   * @param {P} [params] - Os dados a serem enviados no corpo da requisição
   * @param {boolean} [isAsync] - Indica se vai executar de modo assíncrono
   * @returns {Promise<R>} Uma promise que resolve com os dados da resposta
   *
   * @example
   * ```typescript
   * // Criar um novo usuário
   * const novoUsuario = await r3Axios.post<Usuario, { nome: string; email: string }>(
   *   '/api/usuarios',
   *   false,
   *   { nome: 'João', email: 'joao@example.com' }
   * );
   * ```
   */
  abstract post<R, P = object>(
    endpoint: string,
    params?: P,
    isAsync?: boolean,
  ): Promise<R>;

  /**
   * Executa uma requisição PUT para o endpoint especificado.
   *
   * Este método envia uma requisição PUT autenticada para o endpoint da API
   * fornecido, substituindo completamente o recurso no servidor. Se nenhum token
   * de autenticação for encontrado, redirecionará o usuário para a página de login.
   *
   * @template R - O tipo esperado dos dados de resposta em caso de sucesso
   * @template P - O tipo dos parâmetros a serem enviados no corpo da requisição
   * @param {string} endpoint - O endpoint da API para fazer a requisição
   * @param {P} [params] - Os dados a serem enviados no corpo da requisição
   * @param {boolean} [isAsync] - Indica se vai executar de modo assíncrono
   * @returns {Promise<R>} Uma promise que resolve com os dados da resposta
   *
   * @example
   * ```typescript
   * // Atualizar completamente um usuário
   * const usuarioAtualizado = await r3Axios.put<Usuario, Usuario>(
   *   '/api/usuarios/123',
   *   false,
   *   { id: 123, nome: 'João Silva', email: 'joao.silva@example.com' }
   * );
   * ```
   */
  abstract put<R, P = object>(
    endpoint: string,
    params?: P,
    isAsync?: boolean,
  ): Promise<R>;

  /**
   * Executa uma requisição PATCH para o endpoint especificado.
   *
   * Este método envia uma requisição PATCH autenticada para o endpoint da API
   * fornecido, atualizando parcialmente o recurso no servidor. Se nenhum token
   * de autenticação for encontrado, redirecionará o usuário para a página de login.
   *
   * @template R - O tipo esperado dos dados de resposta em caso de sucesso
   * @template P - O tipo dos parâmetros a serem enviados no corpo da requisição
   * @param {string} endpoint - O endpoint da API para fazer a requisição
   * @param {P} [params] - Os dados a serem enviados no corpo da requisição
   * @param {boolean} [isAsync] - Indica se vai executar de modo assíncrono
   * @returns {Promise<R>} Uma promise que resolve com os dados da resposta
   *
   * @example
   * ```typescript
   * // Atualizar parcialmente um usuário
   * const usuarioParcial = await r3Axios.patch<Usuario, { email: string }>(
   *   '/api/usuarios/123',
   *   false,
   *   { email: 'novo.email@example.com' }
   * );
   * ```
   */
  abstract patch<R, P = object>(
    endpoint: string,
    params?: P,
    isAsync?: boolean,
  ): Promise<R>;

  onError() {
    throw new Error("Method not implemented.");
  }
}
